import React, { useState } from 'react';
import { Plus, MessageSquare, User, Clock } from 'lucide-react';
import { addRepNote } from '../services/api';

export default function RepNotesTab({ repId, notes, onNoteAdded }) {
  const [showAddNote, setShowAddNote] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [adding, setAdding] = useState(false);

  const handleAddNote = async () => {
    if (!noteText.trim()) return;
    setAdding(true);
    try {
      const result = await addRepNote(repId, noteText.trim());
      if (result.success) {
        onNoteAdded(result.data.note);
        setNoteText('');
        setShowAddNote(false);
      } else {
        alert(result.error || 'Failed to add note');
      }
    } catch (err) {
      // Note add error handled silently
      alert('Failed to add note');
    } finally {
      setAdding(false);
    }
  };

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <MessageSquare size={20} className="text-primary-900" />
          <h3 className="text-lg font-semibold text-gray-900">Admin Notes</h3>
        </div>
        <button
          onClick={() => setShowAddNote(true)}
          className="bg-primary-900 text-white px-3 py-1.5 rounded-lg text-sm hover:bg-primary-800 transition flex items-center gap-1"
        >
          <Plus size={16} /> Add Note
        </button>
      </div>

      {/* Add Note Modal */}
      {showAddNote && (
        <div className="bg-white border rounded-lg p-4 space-y-3">
          <textarea
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            placeholder="Enter note..."
            className="w-full border rounded px-3 py-2 text-sm resize-none"
            rows={3}
          />
          <div className="flex justify-end gap-2">
            <button
              onClick={() => {
                setShowAddNote(false);
                setNoteText('');
              }}
              className="px-3 py-1 text-sm text-gray-600 hover:text-gray-800 transition"
            >
              Cancel
            </button>
            <button
              onClick={handleAddNote}
              disabled={adding || !noteText.trim()}
              className="bg-green-600 text-white px-4 py-1.5 rounded text-sm hover:bg-green-700 transition disabled:opacity-50 flex items-center gap-1"
            >
              {adding ? 'Adding...' : 'Add Note'}
            </button>
          </div>
        </div>
      )}

      {/* Notes List */}
      {notes.length === 0 ? (
        <div className="text-center py-12 text-gray-400">
          <MessageSquare size={48} className="mx-auto mb-3" />
          <p className="text-sm">No notes yet</p>
        </div>
      ) : (
        <div className="space-y-3">
          {notes.map((note) => (
            <div key={note.id} className="bg-white border rounded-lg p-4">
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <User size={14} className="text-gray-400" />
                  <span className="font-medium">{note.created_by}</span>
                </div>
                <div className="flex items-center gap-1 text-xs text-gray-400">
                  <Clock size={12} />
                  {new Date(note.created_at).toLocaleString()}
                </div>
              </div>
              <p className="text-gray-800 whitespace-pre-wrap">{note.note}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
